import React, { memo, useCallback } from 'react';

import { defineMessage, useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { fg } from '@atlaskit/platform-feature-flags';
import Popup from '@atlaskit/popup';
import { Box, Inline, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { intervalToDatePickerValue, useDateLocale } from '../common/utils/utils';
import {
	ChangeSource,
	type DatePickerValue,
	type FuzzyDatePickerProps,
	type IntervalOption,
} from '../types';

import { DateRange } from './date-range';
import DateRangePickerContent from './date-range-picker-content';

const confirmButtonMessage = defineMessage({
	id: 'townsquare.date-range-picker.confirmation.confirm',
	defaultMessage: 'Confirm',
	description: 'Confirm button label for the date range picker confirmation step',
});

const cancelButtonMessage = defineMessage({
	id: 'townsquare.date-range-picker.confirmation.cancel',
	defaultMessage: 'Cancel',
	description: 'Cancel button label for the date range picker confirmation step',
});

const styles = cssMap({
	confirmationMessage: {
		paddingInline: token('space.250'),
		paddingBlockEnd: token('space.250'),
	},
	confirmButtons: {
		paddingBlockEnd: token('space.250'),
		paddingInline: token('space.250'),
	},
	popupHeader: {
		paddingInline: token('space.250'),
		paddingBlockStart: token('space.250'),
	},
});

export const DateRangePicker: React.MemoExoticComponent<
	(props: FuzzyDatePickerProps) => React.JSX.Element
> = memo((props: FuzzyDatePickerProps): React.JSX.Element => {
	const {
		value,
		defaultValue,
		onChange,
		placeholder,
		onClose,
		withTimeScale = true,
		appearance = 'default',
		disabled = false,
		minDate,
		maxDate,
		granularities,
		intervals,
		showPlaceholderWithSubtleText,
		intervalValue,
		selectIconPlacement = 'end',
		size,
		isRangeSelect,
		confirmationMessage,
		displayConfirmationButtons = false,
		shouldCommitSelection = true,
		subtitle,
		description,
	} = props;

	const { formatMessage } = useIntl();
	const isControlled = 'value' in props;

	const isBundledUpdatesEnabled = fg('goal_types_m3_bundled_updates');

	const [isOpen, setIsOpen] = React.useState(false);
	const [currentValue, setCurentValue] = React.useState<DatePickerValue | undefined>(
		value ?? defaultValue,
	);
	const [selectedInterval, setSelectedInterval] = React.useState<IntervalOption | undefined>(
		intervalValue,
	);
	const [pendingValue, setPendingValue] = React.useState<
		{ value: DatePickerValue; confidence: string; interval?: IntervalOption } | undefined
	>(undefined);
	const locale = useDateLocale();

	React.useEffect(() => {
		if (isControlled) {
			setCurentValue(value);
		}
	}, [value, isControlled]);

	React.useEffect(() => {
		if (fg('mercury_web_enable_intervals')) {
			if (intervalValue) {
				setCurentValue(
					intervalToDatePickerValue(intervalValue, intervalValue.startDate.getFullYear(), locale),
				);
			}
			setSelectedInterval(intervalValue);
		}
	}, [intervalValue, locale]);

	const togglePopup = useCallback(() => {
		if (!disabled) {
			if (isOpen) {
				onClose?.();
			}
			setIsOpen(!isOpen);
		}
	}, [isOpen, onClose, disabled]);

	const closePopup = useCallback(() => {
		setIsOpen(false);
		setPendingValue(undefined);
		onClose?.();
	}, [onClose]);

	const onDateChange = useCallback(
		(
			newValue?: DatePickerValue,
			confidence?: string,
			newSelectedInterval?: IntervalOption,
			source?: ChangeSource,
		) => {
			if (source === ChangeSource.SCALE_SELECT) {
				return;
			}

			if (isBundledUpdatesEnabled && displayConfirmationButtons && newValue && confidence) {
				setPendingValue({ value: newValue, confidence, interval: newSelectedInterval });
				return;
			}

			if (shouldCommitSelection) {
				setCurentValue(newValue);
				setSelectedInterval(newValue !== undefined ? newSelectedInterval : undefined);
			}
			onChange(newValue, confidence, newSelectedInterval);
			setIsOpen(false);
			onClose?.();
		},
		[onChange, onClose, displayConfirmationButtons, isBundledUpdatesEnabled, shouldCommitSelection],
	);

	const onConfirm = useCallback(() => {
		if (!pendingValue) {
			return;
		}
		setCurentValue(pendingValue.value);
		setSelectedInterval(pendingValue.interval);
		onChange(pendingValue.value, pendingValue.confidence, pendingValue.interval);
		setIsOpen(false);
		setPendingValue(undefined);
		onClose?.();
	}, [pendingValue, onChange, onClose]);

	const onClearDate = useCallback(() => {
		onDateChange();
	}, [onDateChange]);

	return (
		<Popup
			zIndex={610}
			placement="bottom-start"
			content={() => (
				<Stack>
					{(subtitle || description) && (
						<Box xcss={styles.popupHeader}>
							{subtitle && (
								<Box>
									<Heading size="xsmall" as="h2">
										{subtitle}
									</Heading>
								</Box>
							)}
							{description && <Box>{description}</Box>}
						</Box>
					)}
					<DateRangePickerContent
						key={`${intervals?.length ?? 0}-${intervals?.[0]?.startDate.getTime() ?? ''}`}
						value={value}
						onChange={onDateChange}
						withTimeScale={withTimeScale}
						minDate={minDate}
						maxDate={maxDate}
						granularities={granularities}
						intervals={intervals}
						isRangeSelect={isRangeSelect}
					/>
					{isBundledUpdatesEnabled && displayConfirmationButtons && pendingValue && (
						<>
							{confirmationMessage && (
								<Box xcss={styles.confirmationMessage}>{confirmationMessage}</Box>
							)}
							<Box xcss={styles.confirmButtons}>
								<Inline space="space.100">
									<Button appearance="primary" onClick={onConfirm}>
										{formatMessage(confirmButtonMessage)}
									</Button>
									<Button appearance="subtle" onClick={closePopup}>
										{formatMessage(cancelButtonMessage)}
									</Button>
								</Inline>
							</Box>
						</>
					)}
				</Stack>
			)}
			isOpen={isOpen && !disabled}
			onClose={closePopup}
			shouldUseCaptureOnOutsideClick
			trigger={(triggerProps) => (
				<DateRange
					onClick={disabled ? undefined : togglePopup}
					value={currentValue}
					placeholder={placeholder}
					triggerProps={triggerProps}
					onClearDate={onClearDate}
					withTimeScale={withTimeScale}
					appearance={appearance}
					disabled={disabled}
					showPlaceholderWithSubtleText={showPlaceholderWithSubtleText}
					intervalDisplayName={selectedInterval?.name}
					selectIconPlacement={selectIconPlacement}
					size={size}
				/>
			)}
		/>
	);
});
